// @flow
import * as React from 'react';
import { QuizStepNumber, QuizStepHeading } from '../app/features';
import { useFormik } from 'formik';
import {
  covertDateToSeverFormat,
  getStoredUser,
  setStoredUser,
} from '@coverright/utils';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { getRoute } from '../app/router';
import { motion } from 'framer-motion';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment/moment';
import { logEvent } from '@coverright/shared/analytics';
import { Gender } from '@coverright/data-access/types/medigap';
import { DateInput, SelectInput } from '../shared';

const schemaFields: any = {
  birthDate: yup
    .string()
    .test('birthDate', 'Birth date is not valid', (value) => {
      if (!value || value.length < 10) {
        return false;
      }
      const val = moment(value);
      return (
        val.isValid() && val.isBetween(moment(new Date(1900, 0, 1)), moment())
      );
    })
    .typeError('Birth date is required')
    .required('Birth date is required'),
  gender: yup.string().required('Gender is required'),
  tobacco: yup.string().required('Tobacco status is required'),
};

export const BasicInfo = () => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const genderWrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [isValid, setIsValid] = useState(!!getStoredUser()?.birthDate && !!getStoredUser()?.gender && getStoredUser()?.tobacco !== undefined);
  const formik = useFormik({
    initialValues: {
      birthDate: getStoredUser()?.birthDate
        ? moment(getStoredUser()!.birthDate, 'YYYY-MM-DD').format('MM/DD/YYYY')
        : '',
      gender: getStoredUser()?.gender || '',
      tobacco: getStoredUser()?.tobacco,
    },
    validationSchema: yup.object(schemaFields),
    onSubmit: (values) => {
      if (formik.isValid) {
        setStoredUser({
          ...getStoredUser(),
          ...values,
          birthDate: covertDateToSeverFormat(values.birthDate),
        });
        navigate(getRoute('personalInfo'));
      }
    },
    validateOnChange: true,
    validateOnMount: true,
  })

  useEffect(() => {
    const {zip, countyName} = (getStoredUser() || {})
    if (!zip || !countyName) {
      navigate(getRoute('zip'));
    }
  }, [navigate])

  useEffect(() => {
    setIsValid(formik.isValid);
    if (formik.isValid) setTimeout(() => buttonRef.current?.focus(), 0);
  }, [formik.isValid]);

  const tobaccoError = formik.touched.tobacco && formik.errors.tobacco;

  return (
    <>
      <QuizStepNumber step={2} completed={isValid} />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
      >
        <QuizStepHeading variant={'h1'} sx={{mb: {xs: 1, sm: 1.5} }} tabIndex={-1}>Tell us about yourself</QuizStepHeading>
        <Typography mb={3.5} color={'text.secondary'}>This helps us personalize options for you.</Typography>

        <form onSubmit={formik.handleSubmit} className={'fill-width'}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <DateInput
                autoFocus
                data-test={'birth-date'}
                error={
                  formik.touched.birthDate && Boolean(formik.errors.birthDate)
                }
                calendarProps={{
                  disableFuture: true,
                }}
                wrapperSx={{flex: 1}}
                helperText={formik.touched.birthDate && formik.errors.birthDate}
                label={'Date of birth'}
                placeholder={'MM / DD / YYYY'}
                id={'birthDate'}
                value={formik.values.birthDate}
                onChange={(e) => {
                  const val = e.target.value as string;
                  formik.setFieldValue('birthDate', val);
                  if (val.length === 10 && moment(val, 'MM/DD/YYYY').isValid()) {
                    setTimeout(() => {
                      const trigger = genderWrapperRef.current?.querySelector<HTMLElement>('.MuiSelect-select');
                      trigger?.focus();
                    }, 0);
                  }
                }}
              />
              <Box ref={genderWrapperRef} sx={{ flex: 1, width: 1 }}>
                <SelectInput
                  fullWidth
                  sx={{pl: .5, '& .MuiInputAdornment-positionEnd': {mr: 1} }}
                  data-test={'gender'}
                  error={formik.touched.gender && Boolean(formik.errors.gender)}
                  helperText={(formik.touched.gender && formik.errors.gender) || ''}
                  label={'Gender'}
                  id={'gender'}
                  value={formik.values.gender}
                  placeholder={'Select gender'}
                  onChange={(event) => {
                    formik.setFieldValue('gender', event.target.value as string);
                    logEvent('click', {
                      element_title: 'Gender',
                      element_id: 'gender',
                      element_type: 'input',
                    });
                  }}
                >
                  <MenuItem data-test={'gender-male'} value={Gender.M}>
                    Male
                  </MenuItem>
                  <MenuItem data-test={'gender-female'} value={Gender.F}>
                    Female
                  </MenuItem>
                </SelectInput>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 1,
                position: 'relative',
              }}
            >
              <Typography fontWeight={600}>Do you use tobacco?</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant={
                    formik.values.tobacco === 'false' ? 'contained' : 'outlined'
                  }
                  color={tobaccoError ? 'error' : 'primary'}
                  data-test={'tobacco-false'}
                  onClick={() => formik.setFieldValue('tobacco', 'false')}
                  fullWidth
                >
                  No
                </Button>
                <Button
                  variant={
                    formik.values.tobacco === 'true' ? 'contained' : 'outlined'
                  }
                  color={tobaccoError ? 'error' : 'primary'}
                  data-test={'tobacco-true'}
                  onClick={() => formik.setFieldValue('tobacco', 'true')}
                  fullWidth
                >
                  Yes
                </Button>
              </Box>
              {tobaccoError && (
                <Typography
                  sx={{
                    color: '#D32C1C',
                    mt: 1,
                  }}
                >
                  Tobacco is required
                </Typography>
              )}
            </Box>
            <Button ref={buttonRef} variant={'contained'}
                    sx={{mt: {xs: 2, sm: 5}, fontWeight: 600}}
                    fullWidth
                    id={'continue-button'}
                    type={'submit'}>
              Continue
            </Button>
          </Box>
        </form>
      </motion.div>
    </>
  );
};
